import BigNumber from 'bignumber.js'

import { Order } from '@blade/std-lib'
import { CurrencyAddress, CurrencyModel } from '@root/entities/currency'
import { Network } from '@root/entities/network'
import { PairAddress } from '@root/entities/pair/model/PairAddress'
import { Provider } from '@root/entities/provider'
import { TxHash } from '@root/entities/transaction'

import * as Maker from './maker'
import { TradeId } from './trade-id'

import type { TradeType } from './trade-type'
import type { ShortTx } from '@blade/blockchain-types'
import type {
  ServicesTxListData,
  WalletsTrackerGetAlertsListData,
} from '@blade/trade-api-client'
import type { ServicesApi } from '@root/shared/api'
import type { AvatarsHost, TokenLogosHost } from '@root/shared/models'

export interface Trade {
  id: TradeId
  type: TradeType
  timestamp: Date
  network: Network.Network
  amountPercentage: number
  transactionHash: TxHash
  maker: Maker.TradeMaker

  totalNative: BigNumber
  totalUsd: BigNumber
  /**
   * Pool market cap at the time of transaction
   */
  marketCapUsd: BigNumber
  liquidityInUsd: BigNumber
  /**
   * Currency price at the time of transaction
   */
  currencyPriceUsd: BigNumber
  currencyAmount: BigNumber
  baseAmount: BigNumber
  pairAddress: PairAddress
  quoteCurrency?: CurrencyModel
  quoteCurrencyAddress: CurrencyAddress
  baseCurrencyAddress: CurrencyAddress
  provider: Provider.Provider
  tokenPriceInNative: BigNumber
  tokenPriceInUsd: BigNumber
}

export const fromDto = (
  dto:
    | ShortTx
    | ServicesApi.TradeTxDto
    | WalletsTrackerGetAlertsListData['data'][number],
  tokenLogosHost: TokenLogosHost,
  avatarsHost: AvatarsHost,
): Trade => {
  return {
    id: TradeId(dto.id),
    pairAddress: PairAddress(dto.pairAddress),
    network: Network.fromDto(dto.network),
    currencyPriceUsd: BigNumber(dto.tokenPriceInUsd),
    currencyAmount: BigNumber(dto.quoteAmount),
    baseAmount: BigNumber(dto.baseAmount),
    baseCurrencyAddress: CurrencyAddress(dto.baseAddress),
    marketCapUsd: BigNumber(dto.marketCap),
    liquidityInUsd: BigNumber(dto.liquidityInUsd),
    totalNative: BigNumber(dto.volumeInNative),
    totalUsd: BigNumber(dto.volumeInUsd),
    timestamp: new Date(dto.timestamp),
    provider: Provider.byId[dto.provider],
    tokenPriceInNative: BigNumber(dto.tokenPriceInNative),
    tokenPriceInUsd: BigNumber(dto.tokenPriceInUsd),
    maker: {
      ...Maker.fromShortTxDto(dto.maker, avatarsHost),
      balanceCurrencyBefore: BigNumber(dto.balanceBefore),
    },
    type: dto.operation,
    transactionHash: TxHash(dto.txSignature),
    amountPercentage: calculateAmountPercentage(dto),
    quoteCurrencyAddress: CurrencyAddress(dto.quoteAddress),
    quoteCurrency:
      'tokenMetadata' in dto && typeof dto.tokenMetadata === 'object'
        ? CurrencyModel.fromTxTokenMeta(
            dto.tokenMetadata,
            Network.fromDto(dto.network),
            tokenLogosHost,
          )
        : undefined,
  }
}

export type MakerActionType =
  | 'buy'
  | 'buy-more'
  | 'buy-first'
  | 'sell'
  | 'sell-all'
  | 'sell-partial'

export const tradeMakerActionType = (
  trade: Trade,
): MakerActionType | undefined => {
  if (trade.type !== 'buy' && trade.type !== 'sell') {
    return undefined
  }

  const { balanceCurrencyBefore, balanceCurrency } = trade.maker
  if (trade.type === 'buy') {
    if (balanceCurrencyBefore.eq(0)) {
      return 'buy-first'
    }
    return 'buy-more'
  }

  if (balanceCurrency.eq(0)) {
    return 'sell-all'
  }

  return 'sell-partial'
}

export const timestamp = (trade: Trade) => trade.timestamp
export const cmpByTimestampAsc = Order.mapInput(Order.Date, timestamp)
export const cmpByTimestampDesc = Order.flip(cmpByTimestampAsc)

const calculateAmountPercentage = (dto: ServicesTxListData['data'][number]) => {
  const isTradeOperation = ['buy', 'sell'].includes(dto.operation)
  if (!isTradeOperation) {
    return 0
  }

  const marketCap = BigNumber(dto.marketCap)
  const quoteAmountToMarketCap = marketCap.minus(BigNumber(dto.quoteAmount))
  const balanceBeforeToMarketCap = marketCap.minus(BigNumber(dto.balanceBefore))
  return quoteAmountToMarketCap
    .dividedBy(balanceBeforeToMarketCap)
    .multipliedBy(100)
    .minus(100)
    .abs()
    .toNumber()
}
